import { ref } from 'vue';
import { makeRequest } from '@/graphs/index';
import { redeemStickerCollectionCode } from '@/graphs/gql';
import type { RedeemStickerCollectionCodeResult } from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';

export type RedeemCodeOutcome =
  | { ok: true; data: RedeemStickerCollectionCodeResult }
  | { ok: false; error: string };

export function useStickerCollectionRedeem() {
  const loading = ref(false);

  async function redeemCode(options: {
    stickerId: string;
    code: string;
    orgId?: string | null;
  }): Promise<RedeemCodeOutcome> {
    const trimmed = options.code.trim();
    if (!trimmed) {
      return { ok: false, error: 'Enter your code.' };
    }

    loading.value = true;
    try {
      const variables: {
        stickerId: string;
        code: string;
        orgId?: string;
      } = {
        stickerId: options.stickerId,
        code: trimmed.toUpperCase(),
      };
      if (options.orgId) {
        variables.orgId = options.orgId;
      }

      const response = await withGraphErrors(makeRequest(
        'mutation',
        redeemStickerCollectionCode,
        variables,
        {},
      ));

      if (response?.errors?.length) {
        const message = response.errors[0]?.message || 'Something went wrong. Please try again.';
        return { ok: false, error: message };
      }

      const data = response?.data?.redeemStickerCollectionCode;
      if (!data) {
        return { ok: false, error: 'Something went wrong. Please try again.' };
      }

      return { ok: true, data };
    } catch (err) {
      console.error('redeemStickerCollectionCode failed:', err);
      return { ok: false, error: 'Something went wrong. Please try again.' };
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    redeemCode,
  };
}
